import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { verifyPlanSubscription, verifyPlanTopup } from '@/api/services/plan';

type VerifyState = 'idle' | 'verifying' | 'success' | 'error';

export default function PlanVerify() {
  const location = useLocation();
  const [state, setState] = useState<VerifyState>('idle');
  const [message, setMessage] = useState<string>('');

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isTopupPath = useMemo(() => location.pathname.includes('/app/plans/topup/verify'), [location.pathname]);

  useEffect(() => {
    const status = searchParams.get('status');
    const txRef = searchParams.get('tx_ref');
    const transactionId = searchParams.get('transaction_id');
    const isTopup = isTopupPath || localStorage.getItem('pendingPlanTopup') === 'true';

    if (!status || !txRef || !transactionId) {
      setState('error');
      setMessage('Missing payment verification parameters.');
      return;
    }

    setState('verifying');
    const verify = isTopup ? verifyPlanTopup : verifyPlanSubscription;
    verify({
      status: String(status),
      tx_ref: String(txRef),
      transaction_id: String(transactionId)
    })
      .then((res) => {
        if (res?.success) {
          if (isTopup) {
            localStorage.setItem('planTopupSuccess', 'true');
          } else {
            localStorage.setItem('planSubscribeSuccess', 'true');
          }
          setState('success');
          setMessage(res?.message || 'Payment verified successfully.');
          return;
        }
        setState('error');
        setMessage(res?.message || 'Verification failed.');
      })
      .catch((err: unknown) => {
        setState('error');
        setMessage(String((err as unknown as Record<string, unknown>)?.message) || 'Verification failed.');
      })
      .finally(() => {
        if (isTopup) {
          localStorage.removeItem('pendingPlanTopup');
        }
      });
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white dark:bg-[#2a2b2e] rounded-2xl shadow p-6 border border-gray-200 dark:border-gray-800 text-center">
        {state === 'verifying' || state === 'idle' ? (
          <>
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-3" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Verifying Payment
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              Please wait while we confirm your transaction.
            </p>
          </>
        ) : state === 'success' ? (
          <>
            <div className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-3">
              ✓
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Payment Successful
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              {message}
            </p>
          </>
        ) : (
          <>
            <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center mx-auto mb-3">
              ×
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              Payment Failed
            </h1>
            <p className="text-sm text-red-600 dark:text-red-400 mt-2">
              {message}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
